import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { IAppState } from "../../types/stores/index";
import { deleteAllItemsInFlow } from "../../actions/flowActions";
import {
    showModal,
    requestMyCompanies,
    CAPageChangeCompany,
    CAPageChangeCompanyNoUnsavedCheck,
} from "../../actions/actionCreators";

import { Popover, MenuItem, Tooltip } from "@material-ui/core";

interface CompanySelectProps {
    // Props
    isUpdateOnChange?: boolean;
    handleFieldChange?: (newCompanyId: number) => void;
}

const CompanySelectSide: React.FC<CompanySelectProps> = ({
    isUpdateOnChange,
    handleFieldChange,
}: CompanySelectProps) => {
    const dispatch = useDispatch();
    const companies = useSelector((state: IAppState) => state.session.companies || []);
    const selectedCompany = useSelector((state: IAppState) => state.session.companyId);
    const isAuthenticated = useSelector((state: IAppState) => state.session.isAuthenticated);
    const flow = useSelector((state: IAppState) => state.flows.byId[state.selected.flow]);
    const activeConnections = useSelector((state: IAppState) => state.fileImportQueue.activeConnections || 0);
    const prevIsAuthenticatedRef = useRef<boolean>(isAuthenticated);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedCompanyName, setSelectedCompanyName] = useState("");
    const [selectedCompanyLogo, setSelectedCompanyLogo] = useState("");

    useEffect(() => {
        if (isAuthenticated && companies.length === 0) {
            requestMyCompanies();
        }
    }, [isAuthenticated, companies]);

    useEffect(() => {
        if (companies && companies.length > 0) {
            const currentCompany = companies.find(company => company.CompanyID === selectedCompany);
            if (currentCompany) {
                setSelectedCompanyName(currentCompany.CompanyName);
                setSelectedCompanyLogo(`/Images/CompanyLogo/${selectedCompany}`);
            }
        }
    }, [companies, selectedCompany]);

    useEffect(() => {
        const prevIsAuthenticated = prevIsAuthenticatedRef.current;
        const isLoggingIn = !prevIsAuthenticated && isAuthenticated;

        if (isLoggingIn) {
            requestMyCompanies();
        }

        prevIsAuthenticatedRef.current = isAuthenticated;
    }, [isAuthenticated]);

    const handleImageLoad = async (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
        const img = event.currentTarget;
        const response = await fetch(img.src);
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onloadend = () => {
            const base64data = reader.result as string;
            const placeholderValue = "R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
            if (base64data.includes(placeholderValue)) {
                setSelectedCompanyLogo("/Images/Icons/default_logo.png"); // Set fallback image
            }
        };
        reader.readAsDataURL(blob);
    };

    const changeCompany = companyIdValue => {
        const newCompanyId = companyIdValue;
        const oldCompanyId = selectedCompany;

        if (isUpdateOnChange) {
            if (flow != null && flow.hasUnsavedChanges) {
                dispatch(CAPageChangeCompany(newCompanyId, oldCompanyId));
            } else {
                dispatch(deleteAllItemsInFlow(-1));
                dispatch(CAPageChangeCompanyNoUnsavedCheck(newCompanyId));
                // dispatch(setSelectedCompany(newCompanyId));
            }
        }
        if (handleFieldChange) {
            dispatch(handleFieldChange(newCompanyId));
            // dispatch(setSelectedCompany(newCompanyId));
        }
    };

    if (!isAuthenticated) {
        return null;
    }

    const handleClick = e => setAnchorEl(e.currentTarget);
    const handleClose = () => setAnchorEl(null);
    const handleMenuItemClick = companyId => {
        if (activeConnections > 0) {
            showModal("FILE_BIG_SIZE_UPLOAD_WARNING", {
                continue: () => changeCompany(companyId),
                type: "Change Company",
            });
        } else {
            changeCompany(companyId);
        }

        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? "simple-popover" : undefined;

    const sortedCompanies = [...companies].sort((a, b) => {
        const nameA = a.CompanyName.toLowerCase();
        const nameB = b.CompanyName.toLowerCase();
        if (nameA < nameB) {
            return -1;
        }
        if (nameA > nameB) {
            return 1;
        }
        return 0;
    });

    const options = sortedCompanies.map(company => (
        <MenuItem
            key={company.CompanyID}
            className="company-select-option"
            value={company.CompanyID}
            selected={company.CompanyID === selectedCompany}
            onClick={() => handleMenuItemClick(company.CompanyID)}
        >
            {company.CompanyName}
        </MenuItem>
    ));

    return (
        <a>
            <Tooltip title={selectedCompanyName} style={{ fontSize: 12 }} placement="right">
                <img
                    className="logo-wayfarer"
                    id={`react_header_logo`}
                    src={`${selectedCompanyLogo}`}
                    alt={`react header logo`}
                    onClick={handleClick}
                    onLoad={handleImageLoad}
                />
            </Tooltip>
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "center",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                }}
                style={{ minWidth: "200px", overflow: "auto", marginLeft: "10px", marginTop: "15px", padding: "200px" }}
            >
                {options}
            </Popover>
        </a>
    );
};

export default CompanySelectSide;
